import { ListingResetError } from './listing-reset'
import type { ListingCursor, FileMetaPart } from './server-bucket-metadata-db'
import type { ListingPage } from './server-synced-bucket'
import type {
  RemoteBucket,
  PutResult,
  DeleteResult,
} from './server-synced-bucket'
import type {
  UFile,
  UFileStream,
  FetchFn,
  OnlineDetector,
  RetryFetchOptions,
} from '@w/utils'
import type { Tel } from '@w/tel'
import { createRelayFetch, insertRelayedTelemetry, nullTel } from '@w/tel'
import { UFile as UFileClass, createOperationTransport } from '@w/utils'

type RemoteBucketFetch = (
  url: string,
  options: RequestInit,
) => Promise<Response>
type Fetch = Window['fetch']

type BucketOperation =
  | { kind: 'put'; file: UFile | UFileStream; tel: Tel }
  | { kind: 'get'; path: string; tel: Tel }
  | { kind: 'path-part'; cursor: ListingCursor; changeSeq: number; offset: number; tel: Tel }
  | { kind: 'files-page'; scope: string; cursor: ListingCursor | null; tel: Tel }
  | { kind: 'delete'; path: string; soft: boolean; tel: Tel }
  | { kind: 'restore'; path: string; tel: Tel }
  | { kind: 'cleanup'; before: number; tel: Tel }

type BucketOperationResult =
  | PutResult
  | DeleteResult
  | UFile
  | ListingPage
  | Uint8Array
  | null
  | void

export interface RemoteBucketClientOptions {
  fetch: RemoteBucketFetch
  tel?: Tel
  logging?: boolean
  retry?: RetryFetchOptions
  onlineDetector?: OnlineDetector
}

type RemoteBucketConnection = { baseUrl: string } | { fetch: Fetch }

export type CreateRemoteBucketClientOptions = RemoteBucketConnection & {
  bucketName: string
  tel?: Tel
  logging?: boolean
  retry?: RetryFetchOptions
  onlineDetector?: OnlineDetector
}

export function createRemoteBucketClient(
  options: CreateRemoteBucketClientOptions,
): RemoteBucketClient {
  const baseUrl = 'baseUrl' in options ? options.baseUrl.replace(/\/$/, '') : ''
  const fetchFn = 'fetch' in options ? options.fetch : globalThis.fetch

  return new RemoteBucketClient({
    fetch: (url, init) =>
      fetchFn(`${baseUrl}/bucket/${options.bucketName}${url}`, {
        ...init,
        credentials: 'include',
      }),
    tel: options.tel,
    logging: options.logging,
    retry: options.retry,
    onlineDetector: options.onlineDetector,
  })
}

export class RemoteBucketClient implements RemoteBucket {
  private readonly transport: ReturnType<
    typeof createOperationTransport<BucketOperation, BucketOperationResult>
  >
  private logging: boolean
  private tel: Tel
  private readonly relayFetches = new WeakMap<Tel, FetchFn>()

  constructor(fetch: RemoteBucketFetch, tel?: Tel, logging?: boolean)
  constructor(options: RemoteBucketClientOptions)
  constructor(
    fetchOrOptions: RemoteBucketFetch | RemoteBucketClientOptions,
    tel?: Tel,
    logging?: boolean,
  ) {
    const options =
      typeof fetchOrOptions === 'function'
        ? { fetch: fetchOrOptions, tel, logging }
        : fetchOrOptions

    this.tel = options.tel ?? nullTel
    this.logging = options.logging ?? false
    this.transport = createOperationTransport<
      BucketOperation,
      BucketOperationResult
    >({
      fetch: options.fetch as FetchFn,
      retry: options.retry,
      onlineDetector: options.onlineDetector,
      execute: (operation, ctx) => this.executeOperation(operation, ctx.fetch),
    })
  }

  private log(msg: string): void {
    if (this.logging) console.info(msg)
  }

  withTel(tel: Tel): RemoteBucketClient {
    const bound = Object.create(this) as RemoteBucketClient
    bound.tel = tel
    return bound
  }

  put(file: UFile | UFileStream, ctx?: { tel?: Tel }): Promise<PutResult> {
    const activeTel = ctx?.tel ?? this.tel
    return activeTel.span(
      {
        name: 'remote-bucket.put',
        type: 'mutation',
        target: 'fetch',
        input: file,
      },
      (spanTel) =>
        this.transport.execute({
          kind: 'put',
          file,
          tel: spanTel,
        }) as Promise<PutResult>,
    )
  }

  get(path: string, ctx?: { tel?: Tel }): Promise<UFile | null> {
    const activeTel = ctx?.tel ?? this.tel
    return activeTel.span(
      {
        name: 'remote-bucket.get',
        type: 'query',
        target: 'fetch',
        input: path,
      },
      (spanTel) =>
        this.transport.execute({
          kind: 'get',
          path,
          tel: spanTel,
        }) as Promise<UFile | null>,
    )
  }

  getFilePathPart(input: { cursor: ListingCursor; changeSeq: number; offset: number }, ctx?: { tel?: Tel }): Promise<Uint8Array | null> {
    return (ctx?.tel ?? this.tel).span(
      { name: 'remote-bucket.getFilePathPart', type: 'query', target: 'fetch', input: undefined },
      spanTel => this.transport.execute({ kind: 'path-part', ...input, tel: spanTel }) as Promise<Uint8Array | null>,
    )
  }

  getFilesPage(input: { scope: string; cursor: ListingCursor | null }, ctx?: { tel?: Tel }): Promise<ListingPage> {
    return (ctx?.tel ?? this.tel).span(
      { name: 'remote-bucket.getFilesPage', type: 'query', target: 'fetch', input: undefined },
      spanTel => this.transport.execute({ kind: 'files-page', ...input, tel: spanTel }) as Promise<ListingPage>,
    )
  }

  delete(
    path: string,
    opts?: { soft?: boolean },
    ctx?: { tel?: Tel },
  ): Promise<DeleteResult> {
    const activeTel = ctx?.tel ?? this.tel
    return activeTel.span(
      {
        name: 'remote-bucket.delete',
        type: 'mutation',
        target: 'fetch',
        input: path,
      },
      (spanTel) =>
        this.transport.execute({
          kind: 'delete',
          path,
          soft: opts?.soft ?? false,
          tel: spanTel,
        }) as Promise<DeleteResult>,
    )
  }

  restore(path: string, ctx?: { tel?: Tel }): Promise<void> {
    const activeTel = ctx?.tel ?? this.tel
    return activeTel.span(
      {
        name: 'remote-bucket.restore',
        type: 'mutation',
        target: 'fetch',
        input: path,
      },
      (spanTel) =>
        this.transport.execute({
          kind: 'restore',
          path,
          tel: spanTel,
        }) as Promise<void>,
    )
  }

  cleanup(olderThanTimestamp: number, ctx?: { tel?: Tel }): Promise<void> {
    const activeTel = ctx?.tel ?? this.tel
    return activeTel.span(
      {
        name: 'remote-bucket.cleanup',
        type: 'mutation',
        target: 'fetch',
        input: olderThanTimestamp,
      },
      (spanTel) =>
        this.transport.execute({
          kind: 'cleanup',
          before: olderThanTimestamp,
          tel: spanTel,
        }) as Promise<void>,
    )
  }

  private async executeOperation(
    operation: BucketOperation,
    baseFetch: FetchFn,
  ): Promise<BucketOperationResult> {
    const fetch = this.getRelayFetch(operation.tel, baseFetch)
    switch (operation.kind) {
      case 'put': {
        const { file } = operation
        if (!(file instanceof UFileClass)) {
          return {
            success: false,
            error: 'UFileStream not supported in RemoteBucketClient',
          }
        }

        this.log(`put path=${file.path}`)
        const formData = new FormData()
        formData.append('path', file.path)
        formData.append('file', file.blob, file.path)
        if (file.meta) {
          formData.append('meta', JSON.stringify(file.meta))
        }

        const response = await fetch('/put', {
          method: 'POST',
          body: formData,
        })

        if (!response.ok) {
          const text = await response.text()
          insertRelayedTelemetry(response)
          return { success: false, error: text || response.statusText }
        }

        const parsed = (await response.json()) as PutResult
        return insertRelayedTelemetry(response, parsed)
      }

      case 'get': {
        this.log(`get path=${operation.path}`)
        const response = await fetch(
          `/get?path=${encodeURIComponent(operation.path)}`,
          {
            method: 'GET',
          },
        )

        if (response.status === 404) {
          insertRelayedTelemetry(response)
          return null
        }

        if (!response.ok) {
          insertRelayedTelemetry(response)
          throw await httpError(`Failed to get file: ${response.status}${response.statusText ? ` ${response.statusText}` : ''}`, response)
        }

        const blob = await response.blob()
        insertRelayedTelemetry(response)
        const metaHeader = response.headers.get('x-file-meta')
        const meta = metaHeader
          ? (JSON.parse(metaHeader) as Record<string, unknown>)
          : undefined

        return new UFileClass(blob, { path: operation.path, meta })
      }

      case 'path-part': {
        const query = new URLSearchParams({ cursor: JSON.stringify(operation.cursor), sequence: String(operation.changeSeq), offset: String(operation.offset) })
        const response = await fetch(`/path-part?${query}`, { method: 'GET' })
        insertRelayedTelemetry(response)
        if (response.status === 404) return null
        if (response.status === 410) throw new ListingResetError()
        if (!response.ok) throw await httpError(`Failed to get path fragment: ${response.status}`, response)
        const part = await readListingBytes(response, 65536)
        if (part.length > 65536) throw new Error('Invalid listing bytes')
        return part
      }

      case 'files-page': {
        const query = new URLSearchParams({ scope: operation.scope, cursor: JSON.stringify(operation.cursor) })
        const response = await fetch(`/files-page?${query}`, { method: 'GET' })
        insertRelayedTelemetry(response)
        if (response.status === 410) throw new ListingResetError()
        if (!response.ok) throw await httpError(`Failed to get files page: ${response.status}`, response)
        const value: unknown = JSON.parse(new TextDecoder().decode(await readListingBytes(response, 1048576)))
        return decodeListingPage(value, operation.scope, operation.cursor)
      }

      case 'delete': {
        this.log(`delete path=${operation.path}`)
        const soft = operation.soft ? 'true' : 'false'
        const response = await fetch(
          `/delete?path=${encodeURIComponent(operation.path)}&soft=${soft}`,
          {
            method: 'DELETE',
          },
        )

        if (!response.ok) {
          const text = await response.text()
          insertRelayedTelemetry(response)
          return { success: false, error: text || response.statusText }
        }

        insertRelayedTelemetry(response)
        return { success: true }
      }

      case 'restore': {
        this.log(`restore path=${operation.path}`)
        const response = await fetch(
          `/restore?path=${encodeURIComponent(operation.path)}`,
          {
            method: 'POST',
          },
        )

        if (!response.ok) {
          insertRelayedTelemetry(response)
          throw await httpError(`Failed to restore file: ${response.status}`, response)
        }
        insertRelayedTelemetry(response)
        return
      }

      case 'cleanup': {
        this.log(`cleanup before=${operation.before}`)
        const response = await fetch(`/cleanup?before=${operation.before}`, {
          method: 'POST',
        })

        if (!response.ok) {
          insertRelayedTelemetry(response)
          throw await httpError(`Failed to cleanup files: ${response.status}`, response)
        }
        insertRelayedTelemetry(response)
      }
    }
  }

  private getRelayFetch(tel: Tel, baseFetch: FetchFn): FetchFn {
    if (tel === nullTel) return baseFetch
    const existing = this.relayFetches.get(tel)
    if (existing) return existing
    const relayed: FetchFn = (input, init) => {
      const originalInput = input
      const absoluteInput =
        typeof input === 'string' && input.startsWith('/')
          ? `http://bucket${input}`
          : input
      const relay = createRelayFetch({
        tel,
        mode: 'native',
        fetch: ((tracedInput: RequestInfo | URL, tracedInit?: RequestInit) => {
          const tracedRequest = new Request(tracedInput)
          return baseFetch(originalInput, {
            ...init,
            ...tracedInit,
            headers: tracedRequest.headers,
            body: tracedRequest.body,
          })
        }),
      })
      return relay(absoluteInput, init)
    }
    this.relayFetches.set(tel, relayed)
    return relayed
  }
}

function record(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

function decodePart(value: unknown, limit: number): Uint8Array {
  if (typeof value !== 'string' || value.length > Math.ceil(limit / 3) * 4) throw new Error('Invalid listing bytes')
  const decoded = atob(value)
  if (decoded.length > limit) throw new Error('Invalid listing bytes')
  return Uint8Array.from(decoded, character => character.codePointAt(0) ?? 0)
}

function decodeListingPage(value: unknown, scope: string, previousCursor: ListingCursor | null): ListingPage {
  if (!record(value) || !Array.isArray(value.items) || value.items.length > 8 || typeof value.complete !== 'boolean' || !record(value.cursor)) throw new Error('Invalid listing page')
  const c = value.cursor
  if (c.scope !== scope || typeof c.epoch !== 'string' || typeof c.after !== 'number' || !Number.isSafeInteger(c.after) || c.after < 0 || typeof c.until !== 'number' || !Number.isSafeInteger(c.until) || c.until < c.after || (value.complete && c.after !== c.until)) throw new Error('Invalid listing cursor')
  const after = c.after
  if (previousCursor && (c.epoch !== previousCursor.epoch || after < previousCursor.after || (previousCursor.after < previousCursor.until && c.until !== previousCursor.until) || (!value.complete && after === previousCursor.after))) throw new Error('Invalid listing cursor')
  if (!value.complete && (after === 0 || after === c.until)) throw new Error('Invalid listing cursor')
  let previous = previousCursor?.after ?? 0
  const items: FileMetaPart[] = value.items.map((item: unknown) => {
    if (!record(item) || typeof item.changeSeq !== 'number' || !Number.isSafeInteger(item.changeSeq) || item.changeSeq <= previous || item.changeSeq > after || typeof item.pathBytes !== 'number' || !Number.isSafeInteger(item.pathBytes) || item.pathBytes < 0 || typeof item.uploadedAt !== 'number' || !Number.isFinite(item.uploadedAt) || typeof item.size !== 'number' || !Number.isFinite(item.size) || item.size < 0 || (item.deletedAt !== null && (typeof item.deletedAt !== 'number' || !Number.isFinite(item.deletedAt)))) throw new Error('Invalid listing record')
    previous = item.changeSeq
    const pathPart = decodePart(item.pathPart, 65536)
    const resourceKey = item.resourceKey === null ? null : decodePart(item.resourceKey, 32)
    if (pathPart.length !== Math.min(65536, item.pathBytes) || (resourceKey !== null && resourceKey.length !== 32)) throw new Error('Invalid listing record bytes')
    return { changeSeq: item.changeSeq, pathBytes: item.pathBytes, pathPart, resourceKey, uploadedAt: item.uploadedAt, size: item.size, deletedAt: item.deletedAt }
  })
  return { items, cursor: { scope, epoch: c.epoch, after: c.after, until: c.until }, complete: value.complete }
}

// Admit bytes before decoding: eight base64 64 KiB path parts fit within 1 MiB.
async function readListingBytes(response: Response, limit: number): Promise<Uint8Array> {
  const reader = response.body?.getReader()
  if (!reader) return new Uint8Array()
  const output = new Uint8Array(limit)
  let length = 0
  try {
    while (true) {
      const chunk = await reader.read()
      if (chunk.done) return output.subarray(0, length)
      if (chunk.value.byteLength > limit - length) {
        await reader.cancel()
        throw new Error('Invalid listing bytes')
      }
      output.set(chunk.value, length)
      length += chunk.value.byteLength
    }
  } finally {
    reader.releaseLock()
  }
}

/** The server's reason (response body) travels as the cause, so error notifications and traces show why. */
async function httpError(message: string, response: Response): Promise<Error> {
  const reason = (await response.text().catch(() => '')).slice(0, 2000)
  return new Error(message, reason ? { cause: new Error(reason) } : undefined)
}
